class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    if (this._updateStickyVisibility) {
      window.removeEventListener("scroll", this._updateStickyVisibility);
      window.removeEventListener("resize", this._updateStickyVisibility);
      this._updateStickyVisibility = null;
    }
    if (this._unsubscribeVariantChange) {
      this._unsubscribeVariantChange();
      this._unsubscribeVariantChange = null;
    }
  }

  initialize() {
    if (this._updateStickyVisibility) {
      window.removeEventListener("scroll", this._updateStickyVisibility);
      window.removeEventListener("resize", this._updateStickyVisibility);
      this._updateStickyVisibility = null;
    }

    const addToCartModule = document.querySelector(".wt-product__add-to-cart");
    if (!addToCartModule || !this.querySelector("button")) return;

    const mainAtcButton =
      addToCartModule.querySelector("button.product-form__submit") ||
      addToCartModule;

    const offset = parseInt(this.dataset.offset, 10) || 0;

    // Delegated so it survives the card being re-rendered on variant change.
    // Adds through the main form's button so cart drawer and apps behave the
    // same; scrolls to the form when that button can't be used.
    if (!this._clickBound) {
      this._clickBound = true;
      this.addEventListener("click", (e) => {
        if (!e.target.closest(".wt-product__sticky-buy__button")) return;
        e.preventDefault();
        if (mainAtcButton.matches("button:not([disabled])")) {
          mainAtcButton.click();
        } else {
          addToCartModule.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });
    }

    if (typeof subscribe === "function" && !this._unsubscribeVariantChange) {
      this._unsubscribeVariantChange = subscribe(
        PUB_SUB_EVENTS.variantChange,
        ({ data }) => {
          const fresh = data?.html?.querySelector(
            "sticky-buy-button .wt-product__sticky-buy__card",
          );
          const card = this.querySelector(".wt-product__sticky-buy__card");
          if (fresh && card) card.innerHTML = fresh.innerHTML;
        },
      );
    }

    this.classList.remove(this.activeClass);

    // PDP v2: the bar shows whenever neither the buy button nor the sizes are on screen (before reaching them and
    // after passing them), on phones and, since 2026-10-20, on desktop too; other products keep the theme's rule.
    const smart = !!this.closest(".pdpm");
    const sizesBox = document.querySelector(".pdpm .wt-product__option__body--tiles");
    // The bar never sits on top of the sizes: it hides while the buy button is on screen or while the
    // sizes are inside the strip the bar would cover.
    const updateStickyVisibility = () => {
      const atcRect = mainAtcButton.getBoundingClientRect();
      let show;
      if (smart) {
        // The layout viewport's height: iOS Safari's toolbar sliding in and out changes window.innerHeight on
        // every scroll, which made the bar flicker when the button or the sizes were near the bottom edge.
        const vh = document.documentElement.clientHeight || window.innerHeight;
        const barH = (this.querySelector(".wt-product__sticky-buy__card")?.offsetHeight || 80) + 16;
        const btnVisible = atcRect.bottom > 0 && atcRect.top < vh - 8;
        const s = sizesBox?.getBoundingClientRect();
        const sizesUnderBar = !!s && s.bottom > vh - barH && s.top < vh;
        show = !btnVisible && !sizesUnderBar;
      } else {
        show = atcRect.bottom <= -offset;
      }
      this.classList.toggle(this.activeClass, show);
      // Lets CSS move floating widgets (chat bubble) out of the card's way.
      document.documentElement.classList.toggle("sticky-atc-visible", show);
    };

    this._updateStickyVisibility = updateStickyVisibility;

    updateStickyVisibility();
    window.addEventListener("scroll", updateStickyVisibility, { passive: true });
    window.addEventListener("resize", updateStickyVisibility);
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.className === "wt-product__img") {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll("img"),
            );
            const index = thumbnails.indexOf(event.target);

            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
